import React, { useState } from 'react';
import {
  Box,
  Fab,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Zoom,
  type SxProps,
  type Theme,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckBox,
  RadioButtonChecked,
  TextFields,
  Numbers,
  Star,
  ToggleOn,
} from '@mui/icons-material';
import { QuestionType, QUESTION_TYPE_CONFIGS } from '../types';
import { CreateQuestionDTO } from '../dtos';

interface AddQuestionButtonProps {
  surveyId?: string; // Optional for template context
  templateId?: string; // Optional for survey context
  insertAfterQuestionId?: string;
  onAdd: (data: CreateQuestionDTO) => Promise<void>;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'floating' | 'inline';
  sx?: SxProps<Theme>; // Allow custom styling from parent
}

export const AddQuestionButton: React.FC<AddQuestionButtonProps> = ({
  surveyId,
  templateId,
  insertAfterQuestionId,
  onAdd,
  disabled = false,
  size = 'medium',
  variant = 'floating',
  sx,
}) => {
  // Ensure we have either surveyId or templateId, but not both
  if (!surveyId && !templateId) {
    throw new Error('AddQuestionButton requires either surveyId or templateId');
  }
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isCreating, setIsCreating] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'CheckBox':
        return <CheckBox />;
      case 'RadioButtonChecked':
        return <RadioButtonChecked />;
      case 'TextFields':
        return <TextFields />;
      case 'Numbers':
        return <Numbers />;
      case 'Star':
        return <Star />;
      default:
        return <ToggleOn />;
    }
  };

  const generateQuestionTitle = (type: QuestionType): Record<string, string> => {
    const defaultTitles: Record<QuestionType, string> = {
      [QuestionType.YES_NO]: 'Yes/No Question',
      [QuestionType.SINGLE_SELECT]: 'Single Choice Question',
      [QuestionType.MULTI_SELECT]: 'Multiple Choice Question',
      [QuestionType.TEXT]: 'Text Question',
      [QuestionType.NUMBER]: 'Number Question',
      [QuestionType.RANGE]: 'Rating Question',
    };

    return {
      en: defaultTitles[type],
      no: defaultTitles[type], // You might want to add Norwegian translations
      de: defaultTitles[type], // You might want to add German translations
    };
  };

  const getDefaultQuestionData = (type: QuestionType): CreateQuestionDTO => {
    const baseData: CreateQuestionDTO = {
      surveyId,
      templateId,
      type,
      title: generateQuestionTitle(type),
      isRequired: false,
      hasCommentField: false,
      insertAfterQuestionId,
    };

    // Add type-specific defaults
    switch (type) {
      case QuestionType.YES_NO:
        return {
          ...baseData,
          options: [
            {
              id: 'yes',
              label: { en: 'Yes', no: 'Ja', de: 'Ja' },
              value: 'yes',
              order: 1,
            },
            {
              id: 'no',
              label: { en: 'No', no: 'Nei', de: 'Nein' },
              value: 'no',
              order: 2,
            },
          ],
        };

      case QuestionType.SINGLE_SELECT:
      case QuestionType.MULTI_SELECT:
        return {
          ...baseData,
          options: [
            {
              id: 'option_1',
              label: { en: 'Option 1' },
              value: 'option_1',
              order: 1,
            },
            {
              id: 'option_2',
              label: { en: 'Option 2' },
              value: 'option_2',
              order: 2,
            },
          ],
        };

      case QuestionType.RANGE:
        return {
          ...baseData,
          rangeConfig: {
            min: 1,
            max: 5,
            displayType: 'scale',
            labels: {
              min: { en: 'Poor' },
              max: { en: 'Excellent' },
            },
          },
        };

      case QuestionType.NUMBER:
        return {
          ...baseData,
          validationRules: {
            minValue: 0,
          },
        };

      default:
        return baseData;
    }
  };

  const handleAddQuestion = async (type: QuestionType) => {
    if (isCreating) return;

    setIsCreating(true);
    handleClose();

    try {
      const questionData = getDefaultQuestionData(type);
      await onAdd(questionData);
    } catch (error) {
      console.error('Failed to add question:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const fabSize = size === 'small' ? 40 : size === 'large' ? 64 : 56;
  const iconSize = size === 'small' ? 'small' : size === 'large' ? 'large' : 'medium';

  const floatingStyles = {
    position: 'relative' as const,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 60,
    my: 1,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 2,
      height: '100%',
      backgroundColor: 'divider',
      opacity: 0.3,
    },
    '&:hover::before': {
      opacity: 0.6,
      backgroundColor: 'primary.main',
    },
  };

  const inlineStyles = {
    display: 'flex',
    justifyContent: 'center',
    py: 2,
  };

  return (
    <Box sx={{ ...(variant === 'floating' ? floatingStyles : inlineStyles), ...sx }}>
      <Zoom in={!isCreating}>
        <Fab
          color="primary"
          size={size}
          onClick={handleClick}
          disabled={disabled || isCreating}
          sx={{
            width: fabSize,
            height: fabSize,
            boxShadow: variant === 'floating' ? 2 : 1,
            '&:hover': {
              boxShadow: variant === 'floating' ? 4 : 2,
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <AddIcon fontSize={iconSize} />
        </Fab>
      </Zoom>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxWidth: 280,
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
            },
          },
        }}
        transformOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            Choose Question Type
          </Typography>
        </Box>

        {QUESTION_TYPE_CONFIGS.map((config) => (
          <MenuItem
            key={config.type}
            onClick={() => handleAddQuestion(config.type)}
            sx={{
              '&:hover': {
                backgroundColor: 'primary.50',
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: 'primary.main',
                minWidth: 40,
              }}
            >
              {getIcon(config.icon)}
            </ListItemIcon>
            <ListItemText
              primary={config.label}
              secondary={config.description}
              secondaryTypographyProps={{
                variant: 'caption',
                color: 'text.secondary',
                sx: { lineHeight: 1.2 },
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
